import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '@env';
import {AuthState} from '@shared/state/auth/auth.state';
import {
  DealerResult,
  FavoriteAction, FinalizeAction,
  InventorySearchResult,
  MarketStatsResult, QuickReportData,
  RawReportData,
  RecallsResult,
  ReportData, QuickReportDataPoints,
  ReportResult,
  ResultWrapper, AdditionalReportMetadata, ServerResponse, FuelEconomyData, ListingResult
} from '@shared/interfaces';
import {Store} from '@ngxs/store';
import {
  collection, Firestore, getDocs, orderBy, query, where, getDoc, doc, onSnapshot
} from '@angular/fire/firestore';
import {child, Database, get, increment, ref, remove, runTransaction, set} from '@angular/fire/database';
import {DocumentSnapshot, FirestoreError} from '@firebase/firestore';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  headers: HttpHeaders;
  apiUrl: string;

  constructor(private http: HttpClient, private store: Store, private firestore: Firestore, private database: Database,
              @Inject(PLATFORM_ID) private platformId: any) {
    this.apiUrl = environment.apiUrl;
  }

  getUrl(url: string): string {
    return `${this.apiUrl}/${url}`;
  }

  createFullReport(reportData: RawReportData): Promise<any> {
    return this.http.post(this.getUrl('report/full-report/create'), {reportData})
      .toPromise();
  }

  createQuickReport(simulatedReportData: QuickReportData, additionalReportMetadata: AdditionalReportMetadata): Promise<any> {
    return this.http.post(this.getUrl('report/quick-report/create'), {
      simulatedReportData,
      additionalReportMetadata
    })
      .toPromise();
  }

  updateQuickReport(additionalReportMetadata: AdditionalReportMetadata): Promise<any> {
    return this.http.post(this.getUrl('report/quick-report/update'), {
      additionalReportMetadata
    })
      .toPromise();
  }

  refreshReport(reportId: string, reportData: RawReportData, action: FinalizeAction): Promise<any> {
    return this.http.post(this.getUrl('report/full-report/refresh'), {reportId, reportData, action})
      .toPromise();
  }

  /**
   * Gets all the reports that a user owns in their document space, it maps the firestore result into a ReportData array
   */
  getUserReports(): Promise<Partial<ReportData>[]> {
    const userId = this.store.selectSnapshot(AuthState.getUserID);

    if (!userId) {
      return Promise.resolve([]);
    }

    const dataCollection = collection(this.firestore, 'reports');
    const createdByClause = where('createdBy', '==', userId);
    // TODO: Remove this once full reports are rolled out
    const reportType = where('reportMetadata.informationLevel', '==', 'quick');
    const isReadyClause = where('reportResultData.resultDeliveryStatus', '==', 'ready');
    const dataQuery = query(dataCollection, createdByClause, reportType, isReadyClause, orderBy('updatedAt'));

    return getDocs(dataQuery)
      .then(documents => {
        const arr: ReportData[] = [];
        documents.forEach(_doc => {
          arr.push({
            ...this.mapDoc(_doc),
            reportOwner: 'user'
          });
        });
        return arr.reverse();
      });
  }

  /**
   * Get public reports in the report collection to show the most recent reports created globally
   */
  getPublicReports(): Promise<ReportData[] | ServerResponse> {
    const dataCollection = collection(this.firestore, 'reports');

    const publicClause = where('visibility', '==', 'public');
    const isReadyClause = where('reportResultData.resultDeliveryStatus', '==', 'ready');
    const orderByUpdatedAt = orderBy('updatedAt');
    let reportQuery = query(dataCollection, publicClause, isReadyClause, orderByUpdatedAt);

    const userId = this.store.selectSnapshot(AuthState.getUserID);
    if (userId) {
      // Get all public records that the user did not create
      const createdByClause = where('createdBy', '!=', userId);
      const orderByCreatedBy = orderBy('createdBy');
      reportQuery = query(dataCollection, publicClause, isReadyClause, createdByClause, orderByCreatedBy, orderByUpdatedAt);
    }

    return getDocs(reportQuery)
      .then(documents => {
        const arr: ReportData[] = [];
        documents.forEach(_doc => {
          arr.push({
            ...this.mapDoc(_doc),
            reportOwner: 'public'
          });
        });
        return arr.reverse();
      });
  }

  /**
   * Makes a call in the user's document and the report's collection to get a specific report by using the associated
   * report id
   * @param reportId
   */
  getReportById(reportId: string): Promise<ReportData | ServerResponse> {
    if (!reportId) {
      return Promise.resolve({});
    }
    // const userId = this.store.selectSnapshot(AuthState.getUserID);

    // const dataDoc = doc(this.firestore, 'reports', reportId);
    // return getDoc(dataDoc).then(document => {
    //   const docInfo = this.mapDoc(document);
    //   if (!docInfo) {
    //     return null;
    //   }
    //   return {
    //     ...docInfo,
    //     reportOwner: docInfo?.createdBy === userId ? 'user' : 'public'
    //   };
    // });

    return this.http.get(this.getUrl('reports/get-report-by-id'), {
      params: {
        reportId
      }
    })
      .toPromise() as Promise<ServerResponse>;
  }

  /**
   * Get NPI data that user has access to. User must be authorized to retrieve NPI data.
   * Relies on firestore rules on the db
   * @param reportId
   * @param npiDocId
   */
  getNpiData(reportId: string, npiDocId: string): Promise<Partial<ReportData>> {
    const userId = this.store.selectSnapshot(AuthState.getUserID);

    if (!userId || !reportId) {
      return Promise.resolve({});
    }
    const path = `reports/${reportId}/npi`;
    const dataDoc = doc(this.firestore, path, npiDocId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  /**
   * Gets all the reports that the user created for that month from firestore
   * @param dateKey
   */
  getReportCreatedList(dateKey: string): Promise<any> {
    const userId = this.store.selectSnapshot(AuthState.getUserID);

    if (!userId) {
      return Promise.resolve({});
    }
    const path = `users/${userId}/reportsCreatedList`;
    const dataDoc = doc(this.firestore, path, dateKey);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  /**
   * Makes a call in the user's document and the report's collection to get a specific report by using the associated
   * report id
   * @param reportId
   * @param docSnapshotCallback
   */
  observeReportById(reportId: string, docSnapshotCallback: {
    next?: (snapshot: DocumentSnapshot<ReportData>) => void;
    error?: (error: FirestoreError) => void;
    complete?: () => void;
  }) {
    const dataDoc = doc(this.firestore, 'reports', reportId);
    return onSnapshot(dataDoc, docSnapshotCallback);
  }

  /**
   * Makes a call in the user's document and the report's collection to get a specific report by using the associated
   * report id. This has to be a snapshot listener because if data gets updated and should be reflected here
   * @param reportId
   * @param resultId
   */
  getResultById(reportId: string, resultId: string): Promise<ReportResult | ServerResponse> {
    const path = `reports/${reportId}/results`;
    const dataDoc = doc(this.firestore, path, resultId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  getFuelEconomyDataById(reportId: string, resultId: string): Promise<{ 'fuelEconomyData': FuelEconomyData[] } | ServerResponse> {
    const path = `reports/${reportId}/fuelEconomyData`;
    const dataDoc = doc(this.firestore, path, resultId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  mapDoc(document) {
    if (document.exists()) {
      return {
        id: document.id,
        ...document.data()
      };
    }
    return null;
  }

  /**
   * Get the simulation result based on the reportId and simulationId (timestamp). This call is made once and not
   * observed
   * @param reportId
   * @param simulationId
   */
  getSimulationResultsById(reportId: string, simulationId: string): Promise<Partial<QuickReportDataPoints>> {
    const path = `reports/${reportId}/simulationResults`;
    const dataDoc = doc(this.firestore, path, simulationId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  /**
   * Get the simulation data points based on the reportId and simulationId (timestamp). This call is made once and not
   * observed
   * @param reportId
   * @param simulationId
   */
  getSimulationDataPointsById(reportId: string, simulationId: string): Promise<Partial<QuickReportDataPoints>> {
    const path = `reports/${reportId}/simulationDataPoints`;
    const dataDoc = doc(this.firestore, path, simulationId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  /**
   * Makes a call in the user's document and the simulation's collection to get a specific simulation by using the
   * associated simulation id
   * @param reportId
   * @param simulationId
   * @param docSnapshotCallback
   */
  observeSimulationResultsById(reportId: string, simulationId: string, docSnapshotCallback: {
    next?: (snapshot: DocumentSnapshot<ReportData>) => void;
    error?: (error: FirestoreError) => void;
    complete?: () => void;
  }) {
    const path = `reports/${reportId}/simulationResults`;
    const dataDoc = doc(this.firestore, path, simulationId);
    return onSnapshot(dataDoc, docSnapshotCallback);
  }

  /**
   * Makes a call in the user's document and the simulation's collection to get a specific simulation by using the
   * associated simulation id
   * @param reportId
   * @param simulationId
   * @param docSnapshotCallback
   */
  observeSimulationDataPointsById(reportId: string, simulationId: string, docSnapshotCallback: {
    next?: (snapshot: DocumentSnapshot<ReportData>) => void;
    error?: (error: FirestoreError) => void;
    complete?: () => void;
  }) {
    const path = `reports/${reportId}/simulationDataPoints`;
    const dataDoc = doc(this.firestore, path, simulationId);
    return onSnapshot(dataDoc, docSnapshotCallback);
  }

  /**
   * Get the vin recalls if any directly from the document, the vinInfo collection and history document are hardcoded
   * as they never change. A ResultWrapper is used to ensure that the document spreader works well
   * The result is an array but sometimes hasData -> false may be there, hence the result wrapper
   * @param reportId
   * @param resultId
   */
  getRecallsForReport(reportId: string, resultId: string): Promise<ResultWrapper<RecallsResult>> {
    if (!reportId || !resultId) {
      return Promise.resolve({});
    }
    const recallPath = `reports/${reportId}/results/${resultId}/vinInfo`;
    const dataDoc = doc(this.firestore, recallPath, 'recall');
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  /**
   * Provide the docId like the activeBySimilarity doc key. This returns the inventory result inside a data key.
   * The result is an array but sometimes hasData -> false may be there, hence the result wrapper
   * @param reportId
   * @param resultId
   * @param sectionId
   */
  getInventoryInfo(reportId: string, resultId: string, sectionId: string): Promise<ListingResult[] | ServerResponse> {
    if (!reportId || !resultId || !sectionId) {
      return Promise.resolve({});
    }
    const inventoryInfoPath = `reports/${reportId}/results/${resultId}/inventoryInfo`;
    const dataCollection = collection(this.firestore, inventoryInfoPath);

    const categoryClause = where('listingCategory', 'array-contains', sectionId);
    const orderByPrice = orderBy('listingDetails.price');
    const inventoryQuery = query(dataCollection, categoryClause, orderByPrice);

    return getDocs(inventoryQuery)
      .then(documents => {
        const arr: ListingResult[] = [];
        documents.forEach(_doc => {
          arr.push({
            ...this.mapDoc(_doc)
          });
        });
        return arr.reverse();
      });
  }


  getInventoryInfoById(reportId: string, resultId: string, vehicleId: string): Promise<ListingResult | ServerResponse> {
    const path = `reports/${reportId}/results/${resultId}/inventoryInfo`;
    const dataDoc = doc(this.firestore, path, vehicleId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  /**
   * Provides info about the market stats like cars sold within specific days.
   * The result is an array but sometimes hasData -> false may be there, hence the result wrapper
   * @param reportId
   * @param resultId
   * @param sectionId
   */
  getMarketStats(reportId: string, resultId: string, sectionId: string): Promise<ResultWrapper<MarketStatsResult>> {
    if (!reportId || !resultId || !sectionId) {
      return Promise.resolve({});
    }
    const path = `reports/${reportId}/results/${resultId}/statsInfo`;
    const dataDoc = doc(this.firestore, path, sectionId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  /**
   * Provides an array of dealerships within the specified filter.
   * The result is an array but sometimes hasData -> false may be there, hence the result wrapper
   * @param reportId
   * @param resultId
   * @param sectionId
   */
  getDealers(reportId: string, resultId: string, sectionId: string): Promise<ResultWrapper<DealerResult[]>> {
    if (!reportId || !resultId || !sectionId) {
      return Promise.resolve({});
    }
    const path = `reports/${reportId}/results/${resultId}/dealerInfo`;
    const dataDoc = doc(this.firestore, path, sectionId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  checkInventoryExistence(quickInfoData: QuickReportData): Promise<any> {
    return this.http.post(this.getUrl('utilities/get-inventory-info'), {
      quickInfoData
    })
      .toPromise();
  }

  async getGasPriceByZipCode(zipcode: string): Promise<any> {
    const path = `gasPricesData/data/${zipcode}`;
    const dbRef = ref(this.database, path);
    return (await get(dbRef)).val();
  }

  async getGasPriceByState(state: string): Promise<any> {
    const path = `gasPricesData/stateData/${state.toUpperCase()}`;
    const dbRef = ref(this.database, path);
    return (await get(dbRef)).val();
  }

  async getElectricityPriceByState(state: string): Promise<any> {
    const path = `electricityPricesData/stateData/${state.toUpperCase()}`;
    const dbRef = ref(this.database, path);
    return (await get(dbRef)).val();
  }

  async getFavoriteReportsId(): Promise<any> {
    const userId = this.store.selectSnapshot(AuthState.getUserID);

    if (!userId) {
      return Promise.resolve({});
    }
    const path = `savedReports/${userId}`;
    const dbRef = ref(this.database, path);
    return (await get(dbRef)).val();
  }

  getPossibleReportsInventory(reportId: string, resultId: string, possibleReportId: string): Promise<InventorySearchResult> {
    if (!reportId || !resultId || !possibleReportId) {
      return Promise.resolve({});
    }
    const path = `reports/${reportId}/results/${resultId}/possibleReportsInventory`;
    const dataDoc = doc(this.firestore, path, possibleReportId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  getListingFilters(reportId: string, resultId: string, sectionId: string): Promise<InventorySearchResult> {
    if (!reportId || !resultId || !sectionId) {
      return Promise.resolve({});
    }
    const path = `reports/${reportId}/results/${resultId}/listingFilters`;
    const dataDoc = doc(this.firestore, path, sectionId);
    return getDoc(dataDoc).then(document => this.mapDoc(document));
  }

  async setFavoriteReport(reportId: string, action: FavoriteAction) {
    const userId = this.store.selectSnapshot(AuthState.getUserID);
    if (!userId) {
      return Promise.reject('User is not logged in');
    }
    const rtDbPath = `savedReports/${userId}`;

    try {
      if (action === 'delete') {
        const dbCounterRef = ref(this.database, `carvryStats/reports/noOfSavedReports`);
        await runTransaction(dbCounterRef, () => increment(-1));

        const dbRef = ref(this.database, rtDbPath);
        return remove(child(dbRef, reportId));
      } else if (action === 'save') {
        const dbCounterRef = ref(this.database, `carvryStats/reports/noOfSavedReports`);
        await runTransaction(dbCounterRef, () => increment(1));

        const dbRef = ref(this.database, rtDbPath);
        return set(child(dbRef, reportId), true);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  }

  handleError(error: any): void {
    if (!error.response || !error.response.data || !error.response.data) {
      throw {message: 'Unknown Error'};
    }
    throw error.response.data.error || error.response.data.message;
  }
}
